import React, { useState, useEffect, useRef } from 'react';
import { Eye } from 'lucide-react';
import { productService } from '../../api/productService';
import { categoryService } from '../../api/categoryService';
import { uploadService } from '../../api/uploadService';
import { MEDIA_BASE_URL } from '../../api/axios';
import HtmlDescriptionEditor from './HtmlDescriptionEditor';
import './ProductForm.css';
import { brandService } from '../../api/brandService';

// Public storefront base URL for "view product" links (no trailing slash).
const STOREFRONT_URL = (process.env.REACT_APP_STOREFRONT_URL || 'https://tech360.ma').replace(/\/+$/, '');

/**
 * Depth-order a flat category list so the product's category <select> shows the
 * full multi-level hierarchy with indentation.
 */
function orderCategoriesByDepth(categories) {
  const childrenByParent = new Map();
  categories.forEach((c) => {
    const key = c.parent_id == null ? 'root' : String(c.parent_id);
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key).push(c);
  });
  const out = [];
  const walk = (key, depth) => {
    (childrenByParent.get(key) || []).forEach((cat) => {
      out.push({ ...cat, depth });
      walk(String(cat.id), depth + 1);
    });
  };
  walk('root', 0);
  // Fallback: surface any category whose parent isn't in the list (orphans).
  if (out.length < categories.length) {
    const seen = new Set(out.map((c) => c.id));
    categories.forEach((c) => {
      if (!seen.has(c.id)) out.push({ ...c, depth: 0 });
    });
  }
  return out;
}

function ProductForm({ product, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    sku: '',
    barcode: '',
    category_id: '',
    brand_id: '',
    variant_name: '',
    variant_group_id: null,
    main_image: '',
    html_description: '',
    bulletin_description: '',
    price: '',
    fake_original_price: '',
    warranty_duration_months: 0,
    low_stock_threshold: 3,
    youtube_link: '',
    is_best_seller: false,
    is_best_price: false,
    show_on_vetrine_home: false,
    is_active: true,
    is_quote_only: false,
    is_price_on_request: false,
  });
  const [initialStock, setInitialStock] = useState({
    quantity: '',
    unit_price: '',
    reference: 'Stock initial',
  });
  const [media, setMedia] = useState([]);
  const [compatibleProducts, setCompatibleProducts] = useState([]);
  const [variantProducts, setVariantProducts] = useState([]);
  const [variantSearch, setVariantSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  // Map of product id → product object for ALL compatible products (regardless of pagination)
  const [compatibleProductDetails, setCompatibleProductDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [compatibilitySearch, setCompatibilitySearch] = useState('');
  const [productsPage, setProductsPage] = useState(1);
  const [productsPerPage] = useState(20);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // The Compatibility and Variants tabs share the same product-list state
  // (allProducts/page/total). This ref lets a debounced fetch verify the user is
  // still on the tab that scheduled it, so a late-firing request can't clobber
  // the other tab's results after a tab switch.
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  // Load the first page immediately when a product-picking tab is opened.
  useEffect(() => {
    if (activeTab === 'compatibility' || activeTab === 'variants') {
      fetchAllProducts(1, activeTab === 'compatibility' ? compatibilitySearch : '');
    }
  }, [activeTab]);

  // Compatibility tab: debounced server-side search.
  useEffect(() => {
    if (activeTab !== 'compatibility') return;

    const timer = setTimeout(() => {
      if (activeTabRef.current === 'compatibility') {
        fetchAllProducts(1, compatibilitySearch);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [compatibilitySearch]);

  // Variants tab: debounced server-side lookup so typing/pasting a name actually
  // queries the backend (not just filters the already-loaded page).
  useEffect(() => {
    if (activeTab !== 'variants') return;

    const timer = setTimeout(() => {
      if (activeTabRef.current === 'variants') {
        fetchAllProducts(1, variantSearch);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [variantSearch]);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        slug: product.slug || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        category_id: product.category_id || '',
        brand_id: product.brand_id || '',
        variant_name: product.variant_name || product.name || '',
        variant_group_id: product.variant_group_id || null,
        main_image: product.main_image || '',
        html_description: product.html_description || '',
        bulletin_description: product.bulletin_description || '',
        price: product.price || '',
        fake_original_price: product.fake_original_price || '',
        warranty_duration_months: product.warranty_duration_months || 0,
        low_stock_threshold: product.low_stock_threshold ?? 3,
        youtube_link: product.youtube_link || '',
        is_best_seller: product.is_best_seller || false,
        is_best_price: product.is_best_price || false,
        show_on_vetrine_home: product.show_on_vetrine_home || false,
        is_active: product.is_active !== undefined ? product.is_active : true,
        is_quote_only: product.is_quote_only || false,
        is_price_on_request: product.is_price_on_request || false,
      });
      setMedia(product.media || []);
      setCompatibleProducts(
        product.compatible_products?.map((p) => p.id) || []
      );
      // Cache full product details so they show in the "currently selected" list
      // even when not present on the current paginated page
      const initialDetails = {};
      (product.compatible_products || []).forEach((p) => {
        initialDetails[p.id] = p;
      });
      setCompatibleProductDetails(initialDetails);
      setVariantProducts(
        product.variants?.map((v) => v.id).filter((id) => id !== product.id) || []
      );
    }
  }, [product]);

  // Whenever we fetch a page of products, augment our local cache so newly-checked
  // products from later pages still render with full info in the selected list.
  useEffect(() => {
    if (!allProducts.length) return;
    setCompatibleProductDetails((prev) => {
      const next = { ...prev };
      allProducts.forEach((p) => {
        if (compatibleProducts.includes(p.id) && !next[p.id]) {
          next[p.id] = p;
        }
      });
      return next;
    });
  }, [allProducts, compatibleProducts]);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getCategories({ per_page: 100 });
      setCategories(response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };
  const fetchBrands = async () => {
    try {
      const response = await brandService.getBrands({ per_page: 100 });
      setBrands(response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchAllProducts = async (page = 1, search = '') => {
    try {
      setLoadingProducts(true);
      const params = {
        page: page,
        per_page: productsPerPage,
      };

      if (search && search.trim()) {
        params.search = search.trim();
      }

      const response = await productService.getProducts(params);
      setAllProducts(response.data || []);
      setTotalProducts(response.total || 0);
      setProductsPage(page);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDescriptionChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      html_description: content,
    }));
  };

  const handleSetMainImage = (mediaItem) => {
    if (mediaItem.type === 'image') {
      setFormData((prev) => ({ ...prev, main_image: mediaItem.url }));
    }
  };

  const handleFileUpload = async (file, type) => {
    setUploading(true);
    try {
      const result = await uploadService.uploadMedia(file, type);
      const newMedia = {
        type: result.type,
        url: result.url,
        is_night_sample: false,
        is_day_sample: false,
      };
      setMedia([...media, newMedia]);
    } catch (err) {
      alert('Échec du téléchargement: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      if (file.type.startsWith('image/')) {
        handleFileUpload(file, 'image');
      }
    });
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      if (file.type.startsWith('video/')) {
        handleFileUpload(file, 'video');
      }
    });
  };

  const handleRemoveMedia = async (index) => {
    const mediaItem = media[index];
    try {
      // Try to delete from server if it's our uploaded file
      if (mediaItem.url.includes('/storage/products/')) {
        await uploadService.deleteMedia(mediaItem.url);
      }
    } catch (err) {
      console.error('Error deleting media:', err);
    }
    setMedia(media.filter((_, i) => i !== index));
  };

  const handleMediaChange = (index, field, value) => {
    const newMedia = [...media];
    newMedia[index][field] = value;
    setMedia(newMedia);
  };

  const handleAddYouTubeLink = () => {
    const link = prompt('Entrez l\'URL YouTube:');
    if (link && link.trim()) {
      setMedia([
        ...media,
        { type: 'youtube', url: link.trim(), is_night_sample: false, is_day_sample: false },
      ]);
    }
  };

  const toggleCompatibleProduct = (productId, productObj) => {
    if (compatibleProducts.includes(productId)) {
      setCompatibleProducts(compatibleProducts.filter((id) => id !== productId));
    } else {
      setCompatibleProducts([...compatibleProducts, productId]);
      if (productObj) {
        setCompatibleProductDetails((prev) => ({ ...prev, [productId]: productObj }));
      }
    }
  };

  const handleInitialStockChange = (e) => {
    const { name, value } = e.target;
    setInitialStock((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Coerce empty strings → null for nullable backend fields
      const blankToNull = (v) =>
        v === '' || v === undefined ? null : v;

      const payload = {
        ...formData,
        barcode: blankToNull(formData.barcode),
        fake_original_price: blankToNull(formData.fake_original_price),
        bulletin_description: blankToNull(formData.bulletin_description),
        youtube_link: blankToNull(formData.youtube_link),
        variant_name: formData.variant_name || null,
        category_id: formData.category_id || null,
        brand_id: formData.brand_id || null,
        low_stock_threshold:
          formData.low_stock_threshold === '' || formData.low_stock_threshold === null
            ? 3
            : parseInt(formData.low_stock_threshold, 10),
        media: media.filter((m) => m.url.trim() !== ''),
        compatible_products: compatibleProducts,
        linked_variant_ids: variantProducts,
      };

      if (!(product && product.id) && initialStock.quantity && parseInt(initialStock.quantity) > 0) {
        payload.initial_stock = parseInt(initialStock.quantity);
        payload.initial_stock_unit_price = parseFloat(initialStock.unit_price) || 0;
        payload.initial_stock_reference = initialStock.reference || 'Stock initial';
      }

      // Update only if we have an actual id (duplicate-mode passes a product without id)
      if (product && product.id) {
        await productService.updateProduct(product.id, payload);
      } else {
        await productService.createProduct(payload);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de l\'enregistrement du produit');
    } finally {
      setLoading(false);
    }
  };

  const getMediaPreview = (item) => {

    if (item.type === 'image') {
      return <img src={ item.url} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
    } else if (item.type === 'video') {
      return <video src={item.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
    } else {
      return <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: '#718096' }}>Vidéo</div>;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="product-form">
      {error && <div className="error-message">{error}</div>}

      {/* Tabs */}
      <div className="form-tabs">
        <button
          type="button"
          className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Informations de base
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          Média ({media.length})
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'pricing' ? 'active' : ''}`}
          onClick={() => setActiveTab('pricing')}
        >
          Prix et stock
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'variants' ? 'active' : ''}`}
          onClick={() => setActiveTab('variants')}
        >
          Variantes {variantProducts.length > 0 ? `(${variantProducts.length})` : ''}
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'compatibility' ? 'active' : ''}`}
          onClick={() => setActiveTab('compatibility')}
        >
          Compatibilité
        </button>
      </div>

      <div className="form-content">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Informations du produit</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Nom du produit *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Entrez le nom du produit"
                  />
                </div>

                <div className="form-group">
                  <label>Slug URL *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    required
                    placeholder="slug-url-produit"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>SKU *</label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    required
                    placeholder="PROD-001"
                  />
                </div>

                <div className="form-group">
                  <label>Code-barres</label>
                  <input
                    type="text"
                    name="barcode"
                    value={formData.barcode}
                    onChange={handleChange}
                    placeholder="1234567890123"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select name="category_id" value={formData.category_id} onChange={handleChange}>
                    <option value="">Sélectionnez une catégorie</option>
                    {orderCategoriesByDepth(categories).map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {`${'   '.repeat(cat.depth)}${cat.depth > 0 ? '└ ' : ''}${cat.name}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Marque</label>
                  <select name="brand_id" value={formData.brand_id} onChange={handleChange}>
                    <option value="">Sélectionnez une marque</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
                </div>

              <div className="form-row" style={{gridTemplateColumns: '1fr'}}>
                <div className="form-group">
                  <label>Lien vidéo YouTube</label>
                  <input
                    type="text"
                    name="youtube_link"
                    value={formData.youtube_link}
                    onChange={handleChange}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description du produit</label>
                <HtmlDescriptionEditor
                  value={formData.html_description}
                  onChange={handleDescriptionChange}
                />
              </div>

              <div className="form-group">
                <label>Bulletin court (affiché sous le statut de stock, max 500 caractères)</label>
                <textarea
                  name="bulletin_description"
                  value={formData.bulletin_description}
                  onChange={handleChange}
                  maxLength={500}
                  rows={3}
                  placeholder="Ex : Livraison 24h dans toutes les villes du Maroc. Garantie 2 ans incluse."
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
                <small style={{ color: '#718096' }}>
                  {(formData.bulletin_description || '').length}/500 caractères
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Durée de garantie (Mois)</label>
                  <input
                    type="number"
                    name="warranty_duration_months"
                    value={formData.warranty_duration_months}
                    onChange={handleChange}
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Seuil d'alerte stock bas</label>
                  <input
                    type="number"
                    name="low_stock_threshold"
                    value={formData.low_stock_threshold}
                    onChange={handleChange}
                    min="0"
                    placeholder="3"
                  />
                  <small style={{ color: '#718096' }}>
                    Une alerte s'affiche dans la liste produits quand stock ≤ ce seuil. Défaut : 3
                  </small>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Options d'affichage</h3>

              <div className="checkbox-grid">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_best_seller"
                    checked={formData.is_best_seller}
                    onChange={handleChange}
                  />
                  <span>Badge meilleure vente</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_best_price"
                    checked={formData.is_best_price}
                    onChange={handleChange}
                  />
                  <span>Badge meilleur prix</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="show_on_vetrine_home"
                    checked={formData.show_on_vetrine_home}
                    onChange={handleChange}
                  />
                  <span>Afficher sur l'accueil vitrine</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />
                  <span>Produit actif</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_quote_only"
                    checked={formData.is_quote_only}
                    onChange={handleChange}
                  />
                  <span>Par devis (prix sur demande)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_price_on_request"
                    checked={formData.is_price_on_request}
                    onChange={handleChange}
                  />
                  <span>Demander prix via WhatsApp</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Média du produit</h3>
              <p style={{ fontSize: '14px', color: '#718096', marginBottom: '16px', padding: '12px', backgroundColor: '#f7fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <strong>Note:</strong> Téléchargez au moins une image et définissez-la comme image principale du produit. L'image principale sera affichée comme photo principale du produit sur le site web.
                {!formData.main_image && <span style={{ color: '#e53e3e', fontWeight: 'bold' }}> (Image principale requise)</span>}
              </p>

              <div className="upload-buttons">
                <label className="upload-btn">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                  Télécharger des images
                </label>

                <label className="upload-btn">
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={handleVideoSelect}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />
                  Télécharger des vidéos
                </label>

                <button
                  type="button"
                  className="upload-btn"
                  onClick={handleAddYouTubeLink}
                  disabled={uploading}
                >
                  Ajouter un lien YouTube
                </button>
              </div>

              {uploading && (
                <div className="uploading-message">
                  Téléchargement... Veuillez patienter
                </div>
              )}

              <div className="media-grid">
                {media.map((item, index) => {
                  const isMainImage = item.type === 'image' && formData.main_image === item.url;
                  return (
                    <div key={index} className={`media-item ${isMainImage ? 'main-image-item' : ''}`}>
                      <div className="media-preview">
                        {getMediaPreview(item)}
                        {isMainImage && (
                          <div className="main-image-badge">Image principale</div>
                        )}
                        <button
                          type="button"
                          className="remove-media-btn"
                          onClick={() => handleRemoveMedia(index)}
                        >
                          ×
                        </button>
                      </div>
                      <div className="media-details">
                        <span className="media-type-badge">{item.type}</span>
                        {item.type === 'image' && (
                          <button
                            type="button"
                            className={`btn-set-main ${isMainImage ? 'is-main' : ''}`}
                            onClick={() => handleSetMainImage(item)}
                            disabled={isMainImage}
                          >
                            {isMainImage ? 'Image principale' : 'Définir comme principale'}
                          </button>
                        )}
                        {item.type === 'video' && (
                          <div className="video-flags">
                            <label className="checkbox-label-small">
                              <input
                                type="checkbox"
                                checked={item.is_night_sample}
                                onChange={(e) =>
                                  handleMediaChange(index, 'is_night_sample', e.target.checked)
                                }
                              />
                              Échantillon de nuit
                            </label>
                            <label className="checkbox-label-small">
                              <input
                                type="checkbox"
                                checked={item.is_day_sample}
                                onChange={(e) =>
                                  handleMediaChange(index, 'is_day_sample', e.target.checked)
                                }
                              />
                              Échantillon de jour
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {media.length === 0 && (
                <div className="empty-state">
                  <p>Aucun média ajouté</p>
                  <p className="empty-state-hint">Téléchargez des images, vidéos ou ajoutez des liens YouTube pour présenter votre produit</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pricing & Stock Tab */}
        {activeTab === 'pricing' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Tarification</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Prix de vente {formData.is_quote_only ? '' : '*'}</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required={!formData.is_quote_only}
                    disabled={formData.is_quote_only}
                    placeholder={formData.is_quote_only ? 'Par devis' : '0.00'}
                  />
                  {formData.is_quote_only && (
                    <small>Ce produit est « Par devis » : aucun prix ne sera affiché sur le site.</small>
                  )}
                </div>

                <div className="form-group">
                  <label>Prix d'origine (pour affichage de promo)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="fake_original_price"
                    value={formData.fake_original_price}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                  <small>Laisser vide si pas en promotion</small>
                </div>
              </div>

              {formData.fake_original_price && formData.price && (
                <div className="price-preview">
                  <span className="original-price">{parseFloat(formData.fake_original_price).toFixed(2)} DH</span>
                  <span className="sale-price">{parseFloat(formData.price).toFixed(2)} DH</span>
                  <span className="discount-badge">
                    {Math.round(
                      ((parseFloat(formData.fake_original_price) - parseFloat(formData.price)) /
                        parseFloat(formData.fake_original_price)) *
                        100
                    )}
                    % de réduction
                  </span>
                </div>
              )}
            </div>

            <div className="form-section">
              <h3>Inventaire</h3>

              {product && product.id ? (
                <div className="form-group">
                  <label>Stock actuel (Lecture seule)</label>
                  <input
                    type="number"
                    value={product.stock_quantity || 0}
                    disabled
                    style={{ background: '#f7fafc', cursor: 'not-allowed' }}
                  />
                  <p style={{ fontSize: '12px', color: '#718096', marginTop: '8px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                    <strong>Note:</strong> Utilisez la page <strong>Mouvements de stock</strong> pour ajouter ou retirer du stock.
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '13px', color: '#4a5568', marginBottom: '16px', padding: '12px', backgroundColor: '#fffbeb', borderRadius: '6px', border: '1px solid #fcd34d' }}>
                    <strong>Stock initial :</strong> Vous pouvez définir une quantité de stock initiale lors de la création. Ce stock sera enregistré comme un mouvement de stock entrant.
                  </p>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Quantité initiale</label>
                      <input
                        type="number"
                        name="quantity"
                        value={initialStock.quantity}
                        onChange={handleInitialStockChange}
                        min="0"
                        placeholder="0"
                      />
                      <small>Laisser vide si pas de stock initial</small>
                    </div>
                    <div className="form-group">
                      <label>Prix unitaire d'achat</label>
                      <input
                        type="number"
                        step="0.01"
                        name="unit_price"
                        value={initialStock.unit_price}
                        onChange={handleInitialStockChange}
                        min="0"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Référence du mouvement</label>
                    <input
                      type="text"
                      name="reference"
                      value={initialStock.reference}
                      onChange={handleInitialStockChange}
                      placeholder="Stock initial"
                      maxLength={100}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Variants Tab */}
        {activeTab === 'variants' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Variantes du produit</h3>
              <p className="section-description">
                Les variantes sont des produits du même type avec des attributs différents (ex: 32GB, 64GB). Ils partagent la même fiche mais ont des prix/stocks distincts.
              </p>

              {/* Current product variant name */}
              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label>Nom de la variante (ce produit)</label>
                <input
                  type="text"
                  name="variant_name"
                  value={formData.variant_name}
                  onChange={handleChange}
                  placeholder="Ex: 32GB, 8GB RAM + 128GB stockage..."
                  maxLength={100}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
                <small style={{ color: '#718096' }}>Laissez vide si ce produit n'est pas une variante</small>
              </div>

              {/* Linked variant products list */}
              {variantProducts.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    Produits liés (variantes)
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {variantProducts.map((variantId) => {
                      const p = allProducts.find((ap) => ap.id === variantId);
                      const displayName = p ? p.name : `Produit #${variantId}`;
                      const displaySku = p ? p.sku : '';
                      const displayVariantName = p ? p.variant_name : '';
                      return (
                        <div
                          key={variantId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 14px',
                            border: '1px solid #bee3f8',
                            borderRadius: '6px',
                            backgroundColor: '#ebf8ff',
                          }}
                        >
                          <div>
                            <span style={{ fontWeight: '500', fontSize: '14px' }}>{displayName}</span>
                            {displayVariantName && (
                              <span style={{ fontSize: '12px', color: '#2b6cb0', marginLeft: '8px', fontWeight: '600' }}>[{displayVariantName}]</span>
                            )}
                            {displaySku && (
                              <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>SKU: {displaySku}</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setVariantProducts(variantProducts.filter((id) => id !== variantId));
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#e53e3e',
                              fontSize: '18px',
                              lineHeight: 1,
                              padding: '0 4px',
                            }}
                            title="Retirer cette variante"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Search to add a variant product */}
              <div>
                <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Ajouter une variante liée
                </label>
                <input
                  type="text"
                  placeholder="Rechercher un produit par nom ou SKU..."
                  value={variantSearch}
                  onChange={(e) => setVariantSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    marginBottom: '12px',
                  }}
                />

                {loadingProducts ? (
                  <div className="uploading-message" style={{ textAlign: 'center', padding: '16px' }}>
                    Chargement des produits...
                  </div>
                ) : (
                  <div className="products-selection-grid">
                    {allProducts
                      .filter((p) => {
                        if (product && p.id === product.id) return false;
                        if (variantProducts.includes(p.id)) return false;
                        if (!variantSearch.trim()) return true;
                        const q = variantSearch.toLowerCase();
                        return p.name.toLowerCase().includes(q) || (p.sku && p.sku.toLowerCase().includes(q));
                      })
                      .map((p) => (
                        <label key={p.id} className="product-checkbox">
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => {
                              setVariantProducts((prev) => [...prev, p.id]);
                            }}
                          />
                          <span>{p.name}{p.variant_name ? ` [${p.variant_name}]` : ''}</span>
                          <small style={{ color: '#718096', fontSize: '12px' }}>SKU: {p.sku}</small>
                        </label>
                      ))}
                  </div>
                )}
              </div>

              <div className="selection-summary" style={{
                marginTop: '16px',
                padding: '12px 16px',
                backgroundColor: variantProducts.length > 0 ? '#f0fdf4' : '#f7fafc',
                border: `1px solid ${variantProducts.length > 0 ? '#86efac' : '#e2e8f0'}`,
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: variantProducts.length > 0 ? '#166534' : '#4a5568'
              }}>
                {variantProducts.length > 0
                  ? `✓ ${variantProducts.length} variante(s) liée(s)`
                  : 'Aucune variante liée'}
              </div>
            </div>
          </div>
        )}

        {/* Compatibility Tab */}
        {activeTab === 'compatibility' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Produits compatibles</h3>
              <p className="section-description">
                Sélectionnez les produits compatibles avec cet article (par ex., accessoires, articles connexes)
              </p>

              {/* Currently selected compatible products — visible regardless of pagination */}
              {compatibleProducts.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    Produits compatibles déjà liés ({compatibleProducts.length})
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {compatibleProducts.map((cid) => {
                      const cp = compatibleProductDetails[cid];
                      const displayName = cp ? cp.name : `Produit #${cid}`;
                      const displaySku = cp ? cp.sku : '';
                      return (
                        <div
                          key={cid}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 14px',
                            border: '1px solid #bee3f8',
                            borderRadius: '6px',
                            backgroundColor: '#ebf8ff',
                          }}
                        >
                          <div>
                            <span style={{ fontWeight: '500', fontSize: '14px' }}>{displayName}</span>
                            {displaySku && (
                              <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
                                SKU: {displaySku}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {cp?.slug && (
                              <a
                                href={`${STOREFRONT_URL}/product/?slug=${encodeURIComponent(cp.slug)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Voir le produit sur le site"
                                aria-label="Voir le produit sur le site"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  color: '#3182ce',
                                  padding: '0 2px',
                                }}
                              >
                                <Eye size={16} />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => toggleCompatibleProduct(cid)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#e53e3e',
                                fontSize: '18px',
                                lineHeight: 1,
                                padding: '0 4px',
                              }}
                              title="Retirer ce produit"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Ajouter d'autres produits compatibles
                </label>
                <input
                  type="text"
                  placeholder="Rechercher des produits par nom, SKU ou code-barres..."
                  value={compatibilitySearch}
                  onChange={(e) => setCompatibilitySearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
                {!loadingProducts && totalProducts > 0 && (
                  <p style={{ fontSize: '12px', color: '#718096', marginTop: '8px' }}>
                    {totalProducts} produit(s) trouvé(s)
                  </p>
                )}
              </div>

              {loadingProducts && (
                <div className="uploading-message" style={{ textAlign: 'center', padding: '20px' }}>
                  Chargement des produits...
                </div>
              )}

              {!loadingProducts && (
                <>
                  <div className="products-selection-grid">
                    {allProducts
                      .filter((p) => !product || p.id !== product.id)
                      .map((p) => (
                        <label key={p.id} className="product-checkbox">
                          <input
                            type="checkbox"
                            checked={compatibleProducts.includes(p.id)}
                            onChange={() => toggleCompatibleProduct(p.id, p)}
                          />
                          <span>{p.name}</span>
                          <small style={{ color: '#718096', fontSize: '12px' }}>SKU: {p.sku}</small>
                        </label>
                      ))}
                  </div>

                  {allProducts.filter((p) => !product || p.id !== product.id).length === 0 && (
                    <div className="empty-state">
                      <p>{compatibilitySearch ? 'Aucun produit trouvé correspondant à votre recherche' : 'Aucun autre produit disponible'}</p>
                    </div>
                  )}

                  {totalProducts > productsPerPage && (
                    <div className="pagination-controls" style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '16px',
                      marginTop: '20px',
                      padding: '16px',
                      borderTop: '1px solid #e2e8f0'
                    }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => fetchAllProducts(productsPage - 1, compatibilitySearch)}
                        disabled={productsPage === 1 || loadingProducts}
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                      >
                        ← Précédent
                      </button>
                      <span style={{ fontSize: '14px', color: '#4a5568' }}>
                        Page {productsPage} / {Math.ceil(totalProducts / productsPerPage)}
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => fetchAllProducts(productsPage + 1, compatibilitySearch)}
                        disabled={productsPage >= Math.ceil(totalProducts / productsPerPage) || loadingProducts}
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                      >
                        Suivant →
                      </button>
                    </div>
                  )}
                </>
              )}

              <div className="selection-summary" style={{
                marginTop: '16px',
                padding: '12px 16px',
                backgroundColor: compatibleProducts.length > 0 ? '#f0fdf4' : '#f7fafc',
                border: `1px solid ${compatibleProducts.length > 0 ? '#86efac' : '#e2e8f0'}`,
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: compatibleProducts.length > 0 ? '#166534' : '#4a5568'
              }}>
                {compatibleProducts.length > 0 ? (
                  <>
                    ✓ {compatibleProducts.length} produit(s) compatible(s) sélectionné(s)
                  </>
                ) : (
                  'Aucun produit compatible sélectionné'
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Annuler
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading || uploading}>
          {loading ? 'Enregistrement...' : product ? 'Mettre à jour le produit' : 'Créer le produit'}
        </button>
      </div>
    </form>
  );
}

export default ProductForm;
